import { Button, Icon } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { OptionFormGroup } from '@/App/tiles/Option/fields/OptionFormGroup.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { selectOptionCurrency, selectOptionLegsProperties } from '@/store/option/selectors/optionSelectors.ts';
import type { FxOption, LegId, OptionType } from '@/store/option/state.ts';

type TypeProps = {
  tileId: string;
};

function selectTypeData(tile: FxOption, legId: LegId) {
  const legProperties = selectOptionLegsProperties(tile, legId);
  const { currency1, currency2 } = selectOptionCurrency(tile);

  return {
    tileId: tile.properties.tileId,
    optionType: legProperties?.type,
    currency1,
    currency2,
    productName: tile.type,
  };
}

export function CallPutType({ tileId }: TypeProps) {
  const [checkOption] = useCheckOption();
  const legId: LegId = `${tileId}/0`;

  const { sgwtWebAnalytics } = useSgwtWidgets();
  const { formatMessage } = useIntl();

  const { currency1, currency2, optionType } = useOptionSelector((state) => selectTypeData(state, legId));

  const handleOptionTypeChange = (value: OptionType) => {
    sgwtWebAnalytics?.trackEvent('Option', 'Change option type', value);

    if (value === 'Call' || value === 'Put') {
      checkOption({
        tileId,
        legProperties: {
          legId,
          type: value,
        },
      });
    }
  };

  return (
    <OptionFormGroup label={<FormattedMessage id="option.fields.type" values={{ inStrategyPanel: true }} />}>
      <input
        data-e2e="putOrCall-input"
        type="text"
        className="form-control form-control-sm bg-primary-alt shadow-none"
        readOnly
        value={formatMessage({ id: `option.type.${optionType ?? 'Call'}` }, { currency1, currency2 })}
      />
      <Button
        size="sm"
        data-e2e="option-type-swap-button"
        purpose="flat"
        color="primary"
        iconPosition="start"
        className="bg-lvl1"
        onClick={() => handleOptionTypeChange(optionType === 'Call' ? 'Put' : 'Call')}
      >
        <Icon icon="swap_horiz" />
      </Button>
    </OptionFormGroup>
  );
}
